/**
 * Frontend SOUL archetype display constant + style parameter definitions.
 *
 * Single source of truth on the frontend side. Mirrors §4.2 of the phase-3b
 * spec; the backend (backend/src/services/souls/catalog.py) must stay in lockstep.
 *
 * Display names MUST match exactly with backend catalog.
 * Translated content (quote, oneLiner, bestFor) lives in i18n locale files
 * under the "souls.{code}" namespace.
 */

export interface Archetype {
  code: string;
  displayName: string;
  description: string;
  defaultParams: SoulParams;
}

export interface SoulParams {
  target_positions: string;
  max_single_weight: string;
  rebalance_direction: string;
  exit_trigger: string;
  turnover_tolerance: string;
}

export const ARCHETYPES: Archetype[] = [
  {
    code: 'barbell',
    displayName: 'Antifragile Barbell',
    description:
      '~85-90% safe base (index/bonds/cash) + ~10-15% speculative sleeve. Trim speculative winners back to target and lock in survival. Hard stops on speculative positions. Nothing in the medium-risk middle.',
    defaultParams: {
      target_positions: '3-5 speculative',
      max_single_weight: '5% (speculative sleeve)',
      rebalance_direction: 'trim winners to fixed split',
      exit_trigger: 'hard stop (speculative)',
      turnover_tolerance: 'low',
    },
  },
  {
    code: 'concentration',
    displayName: 'High-Conviction Concentration',
    description:
      '5-12 high-conviction positions, each sized to matter. Trim on thesis weakening, not on price strength. Winners may run if thesis is intact. Exit only on thesis-break.',
    defaultParams: {
      target_positions: '5-12',
      max_single_weight: '25-30%',
      rebalance_direction: 'hold or add winners',
      exit_trigger: 'thesis-break',
      turnover_tolerance: 'low',
    },
  },
  {
    code: 'factor_tilted',
    displayName: 'Factor-Tilted Systematic',
    description:
      'Tilt toward documented factor exposures (value, quality, momentum, size). Diversified within each tilt. Periodic rules-based rebalancing: trim winners, add laggards. Mechanical exits when a name leaves the factor screen.',
    defaultParams: {
      target_positions: '30-60+',
      max_single_weight: '5%',
      rebalance_direction: 'trim winners, add laggards',
      exit_trigger: 'factor-screen exit',
      turnover_tolerance: 'medium',
    },
  },
  {
    code: 'quality',
    displayName: 'Quality Compounders',
    description:
      'High-ROIC moats held long-term. Very low turnover — trim only when quality erodes (declining margins, falling ROIC, breached moat), not on price. Let compounding run.',
    defaultParams: {
      target_positions: '10-25',
      max_single_weight: '15%',
      rebalance_direction: 'hold winners',
      exit_trigger: 'quality erosion',
      turnover_tolerance: 'very low',
    },
  },
  {
    code: 'momentum',
    displayName: 'Trend / Momentum Rider',
    description:
      'Hold winners, cut losers fast. Add to strength. Highest turnover archetype. Trend-break exits regardless of narrative. Beware sharp momentum crashes.',
    defaultParams: {
      target_positions: '15-30',
      max_single_weight: '8%',
      rebalance_direction: 'add winners, cut losers',
      exit_trigger: 'trend-break',
      turnover_tolerance: 'high',
    },
  },
  {
    code: 'all_weather',
    displayName: 'All-Weather / Risk-Balanced',
    description:
      'Balance risk across environments, not dollars. Rebalance annually to risk parity. No security-level exits — only allocation rebalancing. Accepts bull-market underperformance for downturn protection.',
    defaultParams: {
      target_positions: 'balanced sleeves',
      max_single_weight: 'per risk balance',
      rebalance_direction: 'restore risk balance',
      exit_trigger: 'none (allocation only)',
      turnover_tolerance: 'low',
    },
  },
  {
    code: 'index_plus',
    displayName: 'Index-Plus Core-Satellite',
    description:
      "70-90% low-cost index core + 10-30% capped satellite sleeve. Core rebalances on calendar. Satellite cap enforced so winning bets don't take over. Humblest archetype — contains edge, doesn't amplify it.",
    defaultParams: {
      target_positions: 'core + 3-10 satellites',
      max_single_weight: 'satellite sleeve ≤30%',
      rebalance_direction: 'cap satellites, rebalance core',
      exit_trigger: 'core: none; satellite: varies',
      turnover_tolerance: 'very low',
    },
  },
];

export const ARCHETYPE_CODES = ARCHETYPES.map((a) => a.code);

// Dropdown options for 3 structured params
export const REBALANCE_DIRECTIONS = [
  'hold winners',
  'trim winners',
  'trim winners, add laggards',
  'add winners, cut losers',
  'restore risk balance',
  'cap satellites, rebalance core',
];

export const EXIT_TRIGGERS = [
  'thesis-break',
  'factor-screen exit',
  'quality erosion',
  'trend-break',
  'hard stop',
  'none (allocation only)',
  'core: none; satellite: varies',
];

export const TURNOVER_TOLERANCES = ['very low', 'low', 'medium', 'high'];

/**
 * Check if a given investment style string matches a canonical archetype display name.
 * Mirrors backend logic in catalog.py (normalized lowercase comparison).
 */
export function isArchetypeName(style: string | null): boolean {
  if (!style) return false;
  const normalized = style.toLowerCase().replace(/\s+/g, ' ').trim();
  return ARCHETYPES.some(
    (a) => a.displayName.toLowerCase().replace(/\s+/g, ' ').trim() === normalized
  );
}

/**
 * Get the archetype code for a given display name (case/space insensitive).
 */
export function getArchetypeCode(displayName: string): string | undefined {
  const normalized = displayName.toLowerCase().replace(/\s+/g, ' ').trim();
  return ARCHETYPES.find(
    (a) => a.displayName.toLowerCase().replace(/\s+/g, ' ').trim() === normalized
  )?.code;
}

/**
 * Get the full archetype object by code.
 */
export function getArchetype(code: string): Archetype | undefined {
  return ARCHETYPES.find((a) => a.code === code);
}
